import { useEffect, useMemo, useState, useRef } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/auth';
import { useRole } from '../hooks/useRole';
import { useQuery } from '@tanstack/react-query';
import { getProfile, listRegistrations } from '../api/endpoints';
import { Image, FileText, Tag, BookOpen, Users, ChevronDown, Menu, X } from 'lucide-react';
import { TosConsentDialog, hasTosConsent } from './TosConsentDialog';
import logoSrc from '../img/SB-logo01.jpg';

const UK_QUOTES = [
  'Jeder Mensch hat das Recht auf Kommunikation.',
  'Kommunikation ist ein Menschenrecht.',
  'Alle Menschen sind kompetente Kommunikatoren.',
  'Nicht sprechen zu können bedeutet nicht, nichts zu sagen zu haben.',
  'Teilhabe ist ohne Kommunikation nicht möglich.',
  'Jeder Mensch hat das Recht, sich mitzuteilen – mit allen verfügbaren Mitteln.',
  'Unterstützte Kommunikation ermöglicht Teilhabe.',
  'Kommunikation darf keine Voraussetzung sein, sondern muss ermöglicht werden.',
  'Jeder Mensch hat das Recht, verstanden zu werden und zu verstehen.',
  'Unterstützte Kommunikation gibt Menschen eine Stimme.',
];

export function Layout({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const isLoading = useAuthStore((s) => s.isLoading);
  const navigate = useNavigate();
  const { isAdmin } = useRole();
  const [profileOpen, setProfileOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [quoteHidden, setQuoteHidden] = useState(() => localStorage.getItem('hideUkQuote') === 'true');
  const profileRef = useRef<HTMLDivElement>(null);

  const profileQuery = useQuery({ queryKey: ['profile'], queryFn: getProfile });
  const pendingQuery = useQuery({
    queryKey: ['admin-registrations-count'],
    queryFn: () => listRegistrations('pending'),
    enabled: isAdmin,
    refetchInterval: 60_000,
  });
  const pendingCount = pendingQuery.data?.items?.length ?? 0;
  const displayName = profileQuery.data?.displayName || user?.email || '';
  const avatarKey = profileQuery.data?.avatarKey;

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) {
        setProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const quote = useMemo(() => UK_QUOTES[Math.floor(Math.random() * UK_QUOTES.length)], []);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
          <p className="text-gray-500 font-medium">Wird geladen…</p>
        </div>
      </div>
    );
  }

  const navLink = ({ isActive }: { isActive: boolean }) =>
    `px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
      isActive
        ? 'bg-indigo-100 text-indigo-700'
        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
    }`;

  const mobileNavLink = ({ isActive }: { isActive: boolean }) =>
    `flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
      isActive
        ? 'bg-indigo-100 text-indigo-700'
        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
    }`;

  const navItems = (
    <>
      <NavLink to="/pictograms" className={navLink} onClick={() => setMobileMenuOpen(false)}>
        <span className="flex items-center gap-1.5">
          <Image className="w-4 h-4" />
          Bibliothek
        </span>
      </NavLink>
      <NavLink to="/pages" className={navLink} onClick={() => setMobileMenuOpen(false)}>
        <span className="flex items-center gap-1.5">
          <FileText className="w-4 h-4" />
          Seiten
        </span>
      </NavLink>
      {isAdmin && (
        <NavLink to="/categories" className={navLink} onClick={() => setMobileMenuOpen(false)}>
          <span className="flex items-center gap-1.5">
            <Tag className="w-4 h-4" />
            Themen
          </span>
        </NavLink>
      )}
      {isAdmin && (
        <NavLink to="/sources" className={navLink} onClick={() => setMobileMenuOpen(false)}>
          <span className="flex items-center gap-1.5">
            <BookOpen className="w-4 h-4" />
            Quellen
          </span>
        </NavLink>
      )}
      {isAdmin && (
        <NavLink to="/admin/users" className={navLink} onClick={() => setMobileMenuOpen(false)}>
          <span className="flex items-center gap-1.5">
            <Users className="w-4 h-4" />
            Admin
            {pendingCount > 0 && (
              <span className="bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full font-bold leading-none">{pendingCount}</span>
            )}
          </span>
        </NavLink>
      )}
    </>
  );

  const mobileNavItems = (
    <>
      <NavLink to="/pictograms" className={mobileNavLink} onClick={() => setMobileMenuOpen(false)}>
        <Image className="w-4 h-4" />
        Bibliothek
      </NavLink>
      <NavLink to="/pages" className={mobileNavLink} onClick={() => setMobileMenuOpen(false)}>
        <FileText className="w-4 h-4" />
        Seiten
      </NavLink>
      {isAdmin && (
        <NavLink to="/categories" className={mobileNavLink} onClick={() => setMobileMenuOpen(false)}>
          <Tag className="w-4 h-4" />
          Themen
        </NavLink>
      )}
      {isAdmin && (
        <NavLink to="/sources" className={mobileNavLink} onClick={() => setMobileMenuOpen(false)}>
          <BookOpen className="w-4 h-4" />
          Quellen
        </NavLink>
      )}
      {isAdmin && (
        <NavLink to="/admin/users" className={mobileNavLink} onClick={() => setMobileMenuOpen(false)}>
          <Users className="w-4 h-4" />
          Admin
          {pendingCount > 0 && (
            <span className="bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full font-bold leading-none ml-1">{pendingCount}</span>
          )}
        </NavLink>
      )}
    </>
  );

  // Show ToS consent dialog if profile loaded but not yet accepted
  const showTos = profileQuery.data && !hasTosConsent(profileQuery.data);

  return (
    <div className="min-h-screen bg-gray-50">
      {showTos && <TosConsentDialog userProfile={profileQuery.data!} />}
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            <div className="flex items-center gap-8">
              <Link to="/" className="flex items-center gap-2">
                <img src={logoSrc} alt="Symbolbuch.de Logo" className="w-8 h-8 rounded-lg object-contain" />
                <span className="text-lg font-bold text-gray-900">Symbolbuch<span className="text-indigo-600">.de</span></span>
              </Link>
              {/* Desktop nav links */}
              <div className="hidden md:flex items-center gap-1">
                {navItems}
              </div>
            </div>
            <p
              className="hidden lg:block text-gray-400 italic text-xs truncate max-w-xs select-none group relative cursor-pointer"
              onClick={() => { if (quoteHidden) { setQuoteHidden(false); localStorage.removeItem('hideUkQuote'); } }}
              title={quoteHidden ? 'Zitat wieder einblenden' : undefined}
            >
              {!quoteHidden ? (
                <>
                  „{quote}"
                  <button
                    onClick={(e) => { e.stopPropagation(); setQuoteHidden(true); localStorage.setItem('hideUkQuote', 'true'); }}
                    className="ml-1.5 text-gray-300 hover:text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity"
                    aria-label="Zitat ausblenden"
                    title="Zitat ausblenden"
                  >
                    ×
                  </button>
                </>
              ) : (
                <span className="text-gray-300 hover:text-gray-400 transition-colors">···</span>
              )}
            </p>
            <div className="flex items-center gap-3">
              {/* Profile dropdown */}
              <div className="relative" ref={profileRef}>
                <button
                  onClick={() => setProfileOpen(!profileOpen)}
                  className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors"
                >
                  <div className="w-6 h-6 rounded-full flex items-center justify-center overflow-hidden bg-indigo-600 flex-shrink-0">
                    {avatarKey ? (
                      <img src={`${import.meta.env.VITE_ASSETS_URL}/${avatarKey}`} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-white text-xs font-bold">{(displayName)[0]?.toUpperCase()}</span>
                    )}
                  </div>
                  <span className="text-sm text-gray-700 max-w-[150px] truncate">{displayName}</span>
                  <ChevronDown className="w-3.5 h-3.5 text-gray-400" />
                </button>
                {profileOpen && (
                  <div className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200 py-1 w-48 z-50">
                    <div className="px-4 py-2 border-b border-gray-100">
                      <p className="text-sm font-medium text-gray-900 truncate">{displayName}</p>
                      <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                      <p className="text-xs text-indigo-600 font-medium mt-0.5">
                        {user?.groups?.includes('admin') ? 'Administrator' : user?.groups?.includes('editor') ? 'Editor' : 'Betrachter'}
                      </p>
                    </div>
                    <Link
                      to="/profile"
                      onClick={() => setProfileOpen(false)}
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      Profil bearbeiten
                    </Link>
                    <button
                      onClick={() => { setProfileOpen(false); handleLogout(); }}
                      className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                    >
                      Abmelden
                    </button>
                  </div>
                )}
              </div>
              {/* Hamburger button (mobile) */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors"
                aria-label={mobileMenuOpen ? 'Menü schließen' : 'Menü öffnen'}
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>
        {/* Mobile menu panel */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-gray-100 bg-white px-4 pb-4 pt-2">
            <div className="flex flex-col gap-1">
              {mobileNavItems}
            </div>
          </div>
        )}
      </nav>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {children}
      </main>
      <footer className="border-t border-gray-200 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4 text-xs text-gray-500">
              <Link to="/impressum" className="hover:text-gray-700 transition-colors">Impressum</Link>
              <Link to="/impressum#datenschutz" className="hover:text-gray-700 transition-colors">Datenschutz</Link>
              <Link to="/impressum#nutzungsbedingungen" className="hover:text-gray-700 transition-colors">Nutzungsbedingungen</Link>
              <Link to="/impressum#kontakt" className="hover:text-gray-700 transition-colors">Kontakt</Link>
            </div>
            <p className="text-[10px] text-gray-400">Version {__APP_VERSION__} · Stand {__BUILD_DATE__}</p>
          </div>
          <div className="text-[10px] text-gray-400 mt-3 max-w-2xl leading-relaxed">
            <p>Diese Anwendung unterstützt die Erstellung visueller Kommunikationsmaterialien für den pädagogischen, therapeutischen oder privaten Einsatz.</p>
            <p>Die Inhalte und Funktionen dieser Anwendung ersetzen keine individuelle fachliche, therapeutische, medizinische oder rechtliche Beratung.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
